import React from 'react';
import { Box, Typography, Button, Paper, List, ListItem, ListItemIcon, ListItemText, useTheme, Divider } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import HourglassBottomIcon from '@mui/icons-material/HourglassBottom';
import { PlanData } from '../../../shared/constants/plans';

interface PricingCardProps {
  plan: PlanData;
  billingCycle: 'monthly' | 'annual';
  formatPrice: (value: number) => string;
  yearlyDisplay: (plan: PlanData) => string | null;
  annualSavings: (plan: PlanData) => string | null;
  onContract: () => void;
}

export const PricingCard: React.FC<PricingCardProps> = ({
  plan,
  billingCycle,
  formatPrice,
  yearlyDisplay,
  annualSavings,
  onContract,
}) => {
  const theme = useTheme();

  const price = billingCycle === 'monthly'
    ? formatPrice(plan.priceMonthly)
    : formatPrice(Math.round(plan.priceYearly / 12));

  const badgeLabel = plan.recommended ? 'Mais Popular' : null;
  const savings = annualSavings(plan);

  return (
    <Paper
      elevation={plan.recommended ? 8 : 1}
      sx={{
        p: 4,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        borderRadius: 4,
        position: 'relative',
        border: plan.recommended ? `2px solid ${theme.palette.primary.main}` : '1px solid',
        borderColor: plan.recommended ? theme.palette.primary.main : 'divider',
        transition: 'transform 0.3s ease, box-shadow 0.3s ease',
        '&:hover': {
          transform: 'translateY(-5px)',
          boxShadow: plan.recommended
            ?  '0 20px 40px rgba(236, 64, 122, 0.15)'
            : theme.shadows[8],
        },
      }}
    >
      {/* Badge */}
      {badgeLabel && (
        <Typography
          variant="subtitle1"
          sx={{
            position: 'absolute',
            top: 0,
            left: '50%',
            transform: 'translate(-50%, -50%)',
            backgroundColor: theme.palette.primary.main,
            color: 'white',
            px: 3,
            py: 0.5,
            borderRadius: 2,
            fontWeight: 700,
            whiteSpace: 'nowrap',
          }}
        >
          {badgeLabel}
        </Typography>
      )}

      {/* Plan Name */}
      <Typography
        variant="h5"
        fontWeight="bold"
        textAlign="center"
        gutterBottom
        sx={{ mt: badgeLabel ? 2 : 0 }}
      >
        {plan.name}
      </Typography>

      {/* Description */}
      <Typography
        variant="body2"
        color="text.secondary"
        textAlign="center"
        sx={{ mb: 2, minHeight: 40 }}
      >
        {plan.description}
      </Typography>

      {/* Price */}
      <Box sx={{ textAlign: 'center', mb: 1 }}>
        <Typography
          variant="h3"
          fontWeight="bold"
          component="span"
          sx={{
            fontSize: { xs: '2rem', md: '2.5rem' },
          }}
        >
          {price}
        </Typography>
        <Typography
          variant="subtitle1"
          color="text.secondary"
          component="span"
          sx={{ ml: 1, textTransform: 'none', letterSpacing: 0 }}
        >
          /mês
        </Typography>
      </Box>

      {/* Yearly display */}
      {yearlyDisplay(plan) && (
        <Typography
          variant="body2"
          color="text.secondary"
          textAlign="center"
          sx={{ mb: 0.5 }}
        >
          {yearlyDisplay(plan)}
        </Typography>
      )}

      {/* Savings badge */}
      {savings && (
        <Box sx={{ textAlign: 'center', mb: 2 }}>
          <Typography
            variant="caption"
            sx={{
              backgroundColor: theme.palette.success.main,
              color: '#ffffff',
              px: 1.5,
              py: 0.3,
              borderRadius: 1,
              fontWeight: 700,
              fontSize: '0.7rem',
            }}
          >
            {savings}
          </Typography>
        </Box>
      )}

      <Divider sx={{ my: 2 }} />

      {/* Features */}
      <List sx={{ mb: 3, flexGrow: 1 }} disablePadding>
        {plan.features.map((feature, idx) => {
          const isComingSoon = feature.text.includes('(Em breve)');
          const displayText = feature.text.replace(' (Em breve)', '');
          return (
            <ListItem key={idx} disablePadding sx={{ mb: 1 }}>
              <ListItemIcon sx={{ minWidth: 32 }}>
                {isComingSoon ? (
                  <HourglassBottomIcon
                    sx={{ fontSize: 18, color: theme.palette.warning.main }}
                  />
                ) : feature.included ? (
                  <CheckCircleIcon
                    sx={{ fontSize: 18, color: theme.palette.success.main }}
                  />
                ) : (
                  <CancelIcon
                    sx={{ fontSize: 18, color: theme.palette.text.disabled }}
                  />
                )}
              </ListItemIcon>
              <ListItemText
                primary={
                  <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Typography
                      component="span"
                      variant="body2"
                      sx={{
                        color: feature.included
                          ? theme.palette.text.primary
                          : theme.palette.text.disabled,
                      }}
                    >
                      {displayText}
                    </Typography>
                    {isComingSoon && (
                      <Typography
                        component="span"
                        variant="caption"
                        sx={{
                          backgroundColor: theme.palette.warning.main,
                          color: '#ffffff',
                          px: 0.6,
                          py: 0.1,
                          borderRadius: 0.8,
                          fontWeight: 700,
                          fontSize: '0.6rem',
                          lineHeight: 1.4,
                        }}
                      >
                        Em breve
                      </Typography>
                    )}
                  </Box>
                }
              />
            </ListItem>
          );
        })}
      </List>

      {/* CTA */}
      <Button
        variant={plan.recommended ? 'contained' : 'outlined'}
        size="large"
        fullWidth
        onClick={onContract}
        sx={{
          py: 1.5,
          borderRadius: 2,
          fontWeight: 'bold',
          textTransform: 'none',
          fontSize: '1.1rem',
        }}
      >
        Teste Grátis
      </Button>
    </Paper>
  );
};
